//Built-in libraries

//Third party libraries
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { get, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router';
import { signApplicationPublicAsset } from '@/api/Rover/ApplicationForm';
//Custom components
import Button from '@/components/UI/Button';
import { HookFileInput } from '@/components/UI/HookForm/Form/File';
import { useApplicationFormContext } from '@/features/ApplicationForm/ApplicationFormProvider';
//Services
import { useFileUpload } from '@/hooks/useFileUpload';
import { compareTwoArrays, isObjectEmpty } from '@/utils';

//STP Libraries

//Assets and Miscellaneous

import type { ISignApplicationPublicAssetPayload } from '@/api/Rover/ApplicationForm/types';
import { HookInput } from '@/components/UI/HookForm/Form/Input';
import { HookRadioGroup } from '@/components/UI/HookForm/Form/RadioGroup';
import type { SignedApplicationAssetResponse } from '@/types/Shared';
import BoardMembers from './BoardMembers';

//Component Begin

export default function OrganizationStructure() {
  const params = useParams<string>();
  const [uploadError, setUploadError] = useState<string>('');

  const { upload } = useFileUpload({
    signAsset: signApplicationPublicAsset,
  });

  const { setCurrentStep, updateData, data: formContextData } = useApplicationFormContext();

  const {
    trigger,
    getValues,
    formState: { errors, isValid },
    setFocus,
    watch,
  } = useFormContext();

  const { step4 } = getValues();
  const isChartered = watch('step4.section2.isChartered');
  const isSponsored = watch('step4.section3.isSponsored');
  const q1 = watch('step4.section1.q1');
  const q2 = watch('step4.section2.q2');
  const q3 = watch('step4.section3.q3');
  const providedEvidence = watch('step4.providedEvidence');

  const uploadFile = useMutation(
    (
      payloads: {
        signAssetPayload: ISignApplicationPublicAssetPayload[];
        files: File[];
        field: { key: string; description: string };
      }[]
    ) => Promise.allSettled(payloads.map((p: any) => upload(p))),
    {
      onSuccess: (
        res: any[],
        payloads: {
          signAssetPayload: ISignApplicationPublicAssetPayload[];
          files: File[];
          field: { key: string; description: string };
        }[]
      ) => {
        res.forEach((r, resIndex) => {
          const { files, sign } = r.value;
          let signRes: SignedApplicationAssetResponse[] = [];

          if (Array.isArray(sign)) {
            signRes = [...sign];
          } else {
            signRes = [{ ...sign }];
          }
          updateData({
            step4: {
              ...getValues('step4'),
              [payloads[resIndex].field.key]: {
                ...getValues('step4')[payloads[resIndex].field.key],
                selectedFiles: signRes.map((sig, index) => ({
                  name: files[index].name,
                  path: sig.path,
                  key: sig.key,
                  type: files[index].type,
                  field: payloads[resIndex].field.key,
                  description: payloads[resIndex].field.description,
                })),
              },
              boardMembers: [...formContextData.step4.boardMembers],
            },
          });
        });
        setCurrentStep((prev) => prev + 1);
      },

      onError: (error: any) => {
        console.log(error);
        setUploadError('Failed to upload');
      },
    }
  );

  useEffect(() => {
    if (errors?.step4 && !isObjectEmpty(errors)) {
      const firstErrorKey = Object.keys(errors?.step4)[0];
      const firstErrorChildKey = Object.keys(get(errors, `step4.${firstErrorKey}`))[0];
      const firstErrorChildChildKey = Object.keys(get(errors, `step4.${firstErrorKey}.${firstErrorChildKey}`));
      firstErrorChildChildKey.forEach((d: any) => {
        setFocus(`step4.${firstErrorKey}.${firstErrorChildKey}.${d}`);
      });
    }
  }, [errors, setFocus]);

  const buildFileUploadPayload = () => {
    const formData = getValues('step4');
    const payloads: {
      signAssetPayload: ISignApplicationPublicAssetPayload[];
      files: File[];
      field: { key: string; description: string };
    }[] = [];

    Object.keys(formData).forEach((f: string) => {
      const { selectedFiles } = formData[f];
      if (selectedFiles && selectedFiles.length > 0) {
        const formDataSelectedFiles = selectedFiles.map((f: File) => f.name);
        const formContextDataUploadedFiles: string[] = formContextData?.step4[f].selectedFiles
          .map((sf: File) => sf.name)
          .filter((fn: string | undefined) => fn);

        const fileUploadCompare = compareTwoArrays(formDataSelectedFiles, formContextDataUploadedFiles);

        //Uploading only new file names
        if (fileUploadCompare.array1Diff.length > 0) {
          const signAssetPayload = selectedFiles
            .filter((f: File) => fileUploadCompare.array1Diff.includes(f.name))
            .map((file: File) => ({
              fileName: file.name,
              fileType: file.type,
              fileSize: file.size,
              formId: String(params.applicationFormId),
            }));
          payloads.push({
            signAssetPayload,
            files: selectedFiles,
            field: { key: f, description: f },
          });
        }
      }
    });

    return payloads;
  };

  const onSubmit = async () => {
    trigger();

    if (!isValid) {
      return;
    }
    const formData = getValues('step4');

    const fileUploadPayload = buildFileUploadPayload();
    if (fileUploadPayload.length > 0) {
      uploadFile.mutate(fileUploadPayload);
    } else {
      updateData({
        step4: {
          ...formData,
          boardMembers: [...formContextData.step4.boardMembers],
        },
      });
      setCurrentStep((prev) => prev + 1);
    }
  };

  return (
    <div className='p-6 space-y-6 sm:rounded-r-md sm:overflow-hidden'>
      <h3 className='text-lg leading-6 font-medium text-primaryColor-normal'>Organization Structure</h3>
      <div className='grid grid-cols-6 gap-6'>
        <div className='col-span-6'>
          <h3 className='leading-7 block text-sm font-medium text-neutralColor-medium'>
            Please indicate which document confirm that maintains at least one-third of its governing board's membership for:
          </h3>
          <ul className='list-disc ml-4'>
            <li>residents of low-income neighborhoods, or </li>
            <li>other low-income community residents, or </li>
            <li>elected representatives of low-income neighborhood organizations </li>
          </ul>
          <HookRadioGroup
            name='step4.section1.q1'
            options={[
              {
                label: 'Articles of Incorporation',
                value: 'articlesOfIncorporation',
              },
              { label: 'By-Laws', value: 'byLaws' },
              { label: 'Charter', value: 'charter' },
            ]}
          />
        </div>
        {q1 === 'articlesOfIncorporation' && (
          <div className='col-span-3'>
            <HookInput name='step4.section1.articlesOfIncorporation.section' type='text' label='Section' />
            <HookInput name='step4.section1.articlesOfIncorporation.page' type='text' label='Page' />
            <HookInput name='step4.section1.articlesOfIncorporation.paragraph' type='text' label='Paragraph' />
          </div>
        )}
        {q1 === 'byLaws' && (
          <div className='col-span-3'>
            <HookInput name='step4.section1.byLaws.page' type='text' label='Page' />
            <HookInput name='step4.section1.byLaws.paragraph' type='text' label='Paragraph' />
          </div>
        )}
        {q1 === 'charter' && (
          <div className='col-span-3'>
            <HookInput name='step4.section1.charter.page' type='text' label='Page' />
            <HookInput name='step4.section1.charter.paragraph' type='text' label='Paragraph' />
          </div>
        )}
        <div className='col-span-6'>
          <h3 className='leading-7 block text-sm font-medium text-neutralColor-medium'>
            Provide documentation evidencing that the CHDO provides a formal process for low-income, program beneficiaries to advise the
            organization in all of its decisions regarding the design, siting, development, and management of affordable housing projects
          </h3>
          <HookRadioGroup
            name='step4.providedEvidence'
            options={[
              { label: 'By-Laws', value: 'byLaws' },
              { label: 'Resolutions', value: 'resolutions' },
              {
                label: 'Written Statement',
                value: 'writtenStatement',
              },
            ]}
          />
        </div>
        {providedEvidence === 'byLaws' && (
          <div className='col-span-6'>
            <HookFileInput name='step4.f1.selectedFiles' id='selectedFiles' multiple />
          </div>
        )}
        {providedEvidence === 'resolutions' && (
          <div className='col-span-6'>
            <HookFileInput name='step4.f2.selectedFiles' id='selectedFiles' multiple />
          </div>
        )}
        {providedEvidence === 'writtenStatement' && (
          <>
            <div className='col-span-6'>
              <HookFileInput name='step4.f3.selectedFiles' id='selectedFiles' multiple />
            </div>
            <div className='col-span-3'>
              <HookInput name='step4.f3.section' type='text' label='Section' />
              <HookInput name='step4.f3.page' type='text' label='Page' />
              <HookInput name='step4.f3.paragraph' type='text' label='Paragraph' />
            </div>
          </>
        )}
        {uploadError && <p className='text-tertiaryColor-normal text-md col-span-6'>{uploadError}</p>}
        <div className='col-span-6'>
          <h3 className='leading-7 block text-sm font-medium text-neutralColor-medium'>
            Is the CHDO chartered by a state or local government?
          </h3>
          <HookRadioGroup
            name='step4.section2.isChartered'
            options={[
              { label: 'Yes', value: 'yes' },
              { label: 'No', value: 'no' },
            ]}
          />
        </div>
        {isChartered === 'yes' && (
          <div className='col-span-6'>
            <p className='my-2 text-sm text-neutralColor-normal text-justify'>
              A CHDO may be chartered by a State or local government, but the following restrictions apply: the State or local government
              may not appoint more than one-third of the membership of the organization's governing body ; the board members appointed by
              the State or local government may not, in turn, appoint the remaining two-thirds of the board members; no more than one-third
              of the governing board members are public officials (including any employees of the PJ); and officers and employees of the
              governmental entity can serve as Board members to the CHDO (subject to the one-third appointment limitation), but they cannot
              serve as officers or employees of the CHDO (2013 New HOME Rule requirement).
            </p>
            <HookRadioGroup
              name='step4.section2.q2'
              options={[
                {
                  label: 'Articles of Incorporation',
                  value: 'articlesOfIncorporation',
                },
                { label: 'By-Laws', value: 'byLaws' },
                { label: 'Charter', value: 'charter' },
              ]}
            />
          </div>
        )}
        {q2 === 'articlesOfIncorporation' && (
          <div className='col-span-3'>
            <HookInput name='step4.section2.articlesOfIncorporation.section' type='text' label='Section' />
            <HookInput name='step4.section2.articlesOfIncorporation.page' type='text' label='Page' />
            <HookInput name='step4.section2.articlesOfIncorporation.paragraph' type='text' label='Paragraph' />
          </div>
        )}
        {q2 === 'byLaws' && (
          <div className='col-span-3'>
            <HookInput name='step4.section2.byLaws.page' type='text' label='Page' />
            <HookInput name='step4.section2.byLaws.paragraph' type='text' label='Paragraph' />
          </div>
        )}
        {q2 === 'charter' && (
          <div className='col-span-3'>
            <HookInput name='step4.section2.charter.page' type='text' label='Page' />
            <HookInput name='step4.section2.charter.paragraph' type='text' label='Paragraph' />
          </div>
        )}

        <div className='col-span-6'>
          <h3 className='leading-7 block text-sm font-medium text-neutralColor-medium'>Is CHDO sponsored or created by a for-profit?</h3>
          <HookRadioGroup
            name='step4.section3.isSponsored'
            options={[
              { label: 'Yes', value: 'yes' },
              { label: 'No', value: 'no' },
            ]}
          />
        </div>
        {isSponsored === 'yes' && (
          <HookRadioGroup
            className='col-span-6'
            name='step4.section3.q3'
            options={[
              {
                label: 'Articles of Incorporation',
                value: 'articlesOfIncorporation',
              },
              { label: 'By-Laws', value: 'byLaws' },
              { label: 'Charter', value: 'charter' },
            ]}
          />
        )}
        {q3 === 'articlesOfIncorporation' && (
          <div className='col-span-3'>
            <HookInput name='step4.section3.articlesOfIncorporation.section' type='text' label='Section' />
            <HookInput name='step4.section3.articlesOfIncorporation.page' type='text' label='Page' />
            <HookInput name='step4.section3.articlesOfIncorporation.paragraph' type='text' label='Paragraph' />
          </div>
        )}
        {q3 === 'byLaws' && (
          <div className='col-span-3'>
            <HookInput name='step4.section3.byLaws.page' type='text' label='Page' />
            <HookInput name='step4.section3.byLaws.paragraph' type='text' label='Paragraph' />
          </div>
        )}
        {q3 === 'charter' && (
          <div className='col-span-3'>
            <HookInput name='step4.section3.charter.page' type='text' label='Page' />
            <HookInput name='step4.section3.charter.paragraph' type='text' label='Paragraph' />
          </div>
        )}
        <div className='col-span-6'>
          <BoardMembers formValues={step4} />
        </div>
      </div>
      <div className='flex gap-4 justify-end mt-5'>
        <Button label='Previous' btnStyle='neutral' type='button' onClick={() => setCurrentStep((prev) => prev - 1)} />
        <Button
          label={uploadFile.isLoading ? 'Uploading Files...' : 'Next'}
          btnStyle={uploadFile?.isLoading ? 'neutral' : 'primary'}
          disabled={uploadFile.isLoading}
          type='button'
          onClick={onSubmit}
        />
      </div>
    </div>
  );
}
